import React, { useEffect, useMemo, useState } from 'react';
import {
  PageSection,
  Title,
  EmptyState,
  EmptyStateVariant,
  EmptyStateBody,
  Text,
  TextContent,
  TextVariants,
  Flex,
  FlexItem,
} from '@patternfly/react-core';
import { CreateSharedDeployment, DeploymentsTable, CreateDedicatedDeployment } from './components';
import { ClusterRepresentation, useGetDeployments } from 'src/query/deployments';
import { useGetClusters } from 'src/query/deployments';
import { useKeycloak } from '@react-keycloak/web';
import { CreateCluster, StatusBadge } from '../Clusters/components';
import { ExceptionAlert, WarningAlert } from '../Clusters/components/status-alerts';
import { BlueBadge, PurpleBadge } from '@app/components/StatusBadge/status-badge';
import { ClusterActionDropdown } from '../Clusters/components/cluster-action-dropdown';
import ClusterDeployments from '../Clusters/components/cluster-deployments';
import { baseUrl } from 'src';
import { ServerGroupIcon } from '@patternfly/react-icons';
import { IconCluster } from '@app/components/icons/icon-cluster';

export interface IDeploymentsProps {
  sampleProp?: string;
}

export const deploymentModalKey = 'p2_selfserve_create_deployment_modal';

const Deployments: React.FC<IDeploymentsProps> = () => {
  const { keycloak } = useKeycloak();
  const [refresh, setRefresh] = useState('');
  const [isCreateSharedDeploymentModalOpen, setIsCreateSharedDeploymentModalOpen] = useState<
    ClusterRepresentation | boolean
  >(false);
  const [isCreateClusterModalOpen, setIsCreateClusterModalOpen] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(deploymentModalKey) === null) {
      localStorage.setItem(deploymentModalKey, 'false');
    }
  }, []);

  // Only gets the "self deployments"
  const {
    data: deploys = [],
    error: deploymentsError,
    isLoading,
    isFetched,
    isError: isDeploymentsError,
    refetch,
  } = useGetDeployments({
    pathParams: { realm: 'self' },
  });

  const {
    data: clusters = [],
    error: clustersError,
    isLoading: clustersLoading,
    isFetched: isClustersFetched,
    refetch: clusterRefetch,
  } = useGetClusters({
    pathParams: { realm: 'self' },
  });

  const hasNoDeploymentsByUser = useMemo(() => {
    if (isLoading && clustersLoading) return false;
    if (clusters.length > 0) return false; // Dedicated clusters exist

    // No deploys at all
    if (deploys.length === 0) return true;

    // Deployments made by user (possible deployments for this organization exist but made by someone else)
    const deploymentsByUser = deploys.map((deploy) => deploy.created_by_user_id === keycloak.tokenParsed?.sub);
    return deploymentsByUser.length === 0;
  }, [deploys, keycloak.tokenParsed?.sub, clusters, isLoading, clustersLoading]);

  useEffect(() => {
    if (
      isFetched &&
      isClustersFetched &&
      localStorage.getItem(deploymentModalKey) === 'false' &&
      deploys.length === 0 && // No shared deployments
      clusters.length === 0 && // No dedicated clusters
      !isDeploymentsError
    ) {
      // open modal
      setIsCreateSharedDeploymentModalOpen(true);
      localStorage.setItem(deploymentModalKey, 'true');
    }
  }, [isFetched, isClustersFetched, deploys, clusters, isDeploymentsError]);

  const sharedDeploys = deploys.filter((deploy) => deploy.cluster.variant === 'SHARED');

  const SharedDeployments = (
    <div>
      <Flex style={{ marginBottom: '1rem' }} justifyContent={{ default: 'justifyContentSpaceBetween' }}>
        <FlexItem>
          <Flex alignItems={{ default: 'alignItemsCenter' }}>
            <IconCluster height="30px" width="30px" />
            <BlueBadge label="free" title="as in beer" />
            <Title headingLevel="h2">Phase Two Cluster</Title>
          </Flex>
        </FlexItem>
        <FlexItem>
          <CreateSharedDeployment
            refresh={setRefresh}
            isModalOpen={isCreateSharedDeploymentModalOpen}
            setModalOpen={setIsCreateSharedDeploymentModalOpen}
          />
        </FlexItem>
      </Flex>
      {/* Needs to only be shared deployments */}
      <DeploymentsTable
        refresh={refresh}
        deploys={sharedDeploys}
        error={deploymentsError}
        isLoading={isLoading}
        refetch={refetch}
        emptyStateMessage={{
          title: hasNoDeploymentsByUser ? 'No free deployment found.' : '',
          body: hasNoDeploymentsByUser ? 'Create a free deployment to get started.' : 'No Deployments found.',
        }}
      />
    </div>
  );

  const DedicatedClusters = clusters.map((cluster) => {
    const { status } = cluster;

    let body = <WarningAlert />;
    if (status === 'PENDING_PAYMENT' || status === 'PROVISIONING' || status === 'BILLING_SETUP') {
      body = <WarningAlert />;
    } else if (status === 'SETUP_EXCEPTION') {
      body = <ExceptionAlert />;
    } else {
      body = <ClusterDeployments refresh={refresh} clusterId={cluster.id} />;
    }
    return (
      <div key={cluster.id} style={{ marginTop: '3.5rem' }}>
        <Flex style={{ marginBottom: '1rem' }} justifyContent={{ default: 'justifyContentSpaceBetween' }}>
          <FlexItem>
            <Flex alignItems={{ default: 'alignItemsCenter' }}>
              <ServerGroupIcon height="30px" width="30px" />
              <PurpleBadge label="dedicated" />
              {StatusBadge(cluster.status)}
              <Title headingLevel="h2">
                {cluster.name} <span style={{ marginLeft: '4px', opacity: '0.3' }}>({cluster.region})</span>
              </Title>
            </Flex>
          </FlexItem>
          <FlexItem>
            <Flex alignItems={{ default: 'alignItemsCenter' }}>
              <CreateDedicatedDeployment key={cluster.id} refresh={setRefresh} cluster={cluster} />
              <ClusterActionDropdown cluster={cluster} refetch={clusterRefetch} />
            </Flex>
          </FlexItem>
        </Flex>
        {body}
      </div>
    );
  });

  let DeploymentsBody = (
    <>
      {SharedDeployments}
      {DedicatedClusters}
    </>
  );
  if (clusters.length > 0) {
    DeploymentsBody = (
      <>
        {DedicatedClusters}
        <div style={{ marginTop: '3.5rem' }}>{SharedDeployments}</div>
      </>
    );
  }

  return (
    <PageSection>
      <div>
        <Flex
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsCenter' }}
          style={{ borderBottom: '1px solid #efefef', paddingBottom: '1rem', marginBottom: '1rem' }}
        >
          <Title headingLevel="h1" size="2xl">
            Deployments
          </Title>
          {
            <CreateCluster
              isModalOpen={isCreateClusterModalOpen}
              setModalOpen={setIsCreateClusterModalOpen}
              clusters={clusters}
            />
          }
        </Flex>
        <TextContent style={{ marginBottom: '1rem' }}>
          <Text component="p">
            Visit{' '}
            <a href={`${baseUrl}/${keycloak.realm}/portal/organizations`} target="_blank" rel="noreferrer">
              Org Admin
            </a>{' '}
            to manage your team, invite other users, configure SSO for multiple organizations, adjust your profile and
            more.
          </Text>
        </TextContent>
      </div>
      {hasNoDeploymentsByUser && (
        <EmptyState variant={EmptyStateVariant.full}>
          <EmptyStateBody>
            <TextContent>
              <Text component="p">
                Each account can create a deployment using our free plan for testing and development.
              </Text>
              <Text component={TextVariants.small}>Get started with your free deployment today!</Text>
            </TextContent>
          </EmptyStateBody>
        </EmptyState>
      )}

      {DeploymentsBody}
    </PageSection>
  );
};

export { Deployments };
